import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, Globe, House, ShieldCheck } from "@phosphor-icons/react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { supportedLanguages } from "@/i18n";
import TurnstileWidget from "@/components/TurnstileWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const COUNTRIES = {
  brazil: { name: "Brazil", nationality: "Brazilian", language: "pt" },
  "united-kingdom": { name: "United Kingdom", nationality: "British", language: "en" },
  "united-states": { name: "United States", nationality: "American", language: "en" },
  canada: { name: "Canada", nationality: "Canadian", language: "en" },
  australia: { name: "Australia", nationality: "Australian", language: "en" },
  "new-zealand": { name: "New Zealand", nationality: "New Zealander", language: "en" },
  china: { name: "China", nationality: "Chinese", language: "zh" },
  france: { name: "France", nationality: "French", language: "fr" },
  germany: { name: "Germany", nationality: "German", language: "de" },
  italy: { name: "Italy", nationality: "Italian", language: "it" },
  spain: { name: "Spain", nationality: "Spanish", language: "es" },
  international: { name: "International", nationality: "", language: "en" },
};

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  nationality: "",
  current_country: "",
  budget_min: "",
  budget_max: "",
  locations: "",
  property_types: "",
  purchase_timeframe: "",
  financing_method: "",
  purchase_purpose: "",
  message: "",
  referral_code: "",
  gdpr_consent: false,
  website: "",
};

const fieldClass =
  "mt-1.5 h-11 rounded-sm border-slate-300 focus-visible:ring-1 focus-visible:ring-[#C5A059]";

export default function InternationalBuyerFinder() {
  const { country = "international" } = useParams();
  const { t, i18n } = useTranslation();
  const campaign = COUNTRIES[country] || COUNTRIES.international;

  const [form, setForm] = useState({
    ...INITIAL_FORM,
    nationality: campaign.nationality,
    current_country: campaign.name === "International" ? "" : campaign.name,
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [status, setStatus] = useState({ loading: false, error: "", result: null });

  const language = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

  useEffect(() => {
    if (!localStorage.getItem("panatlantik_language") && campaign.language) {
      i18n.changeLanguage(campaign.language);
    }
  }, [campaign.language, i18n]);

  useEffect(() => {
    const title = t(
      "buyerFinder.seoTitle",
      "Buy Property in Portugal | Panatlantik International"
    );
    const description = t(
      "buyerFinder.seoDescription",
      "Register your requirements and receive carefully matched property opportunities in Portugal."
    );

    document.title = title;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);
  }, [t, language, country]);

  const set = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const selectOptions = useMemo(
    () => ({
      propertyTypes: [
        ["Apartment", t("buyerFinder.apartment", "Apartment")],
        ["House", t("buyerFinder.house", "House")],
        ["Villa", t("buyerFinder.villa", "Villa")],
        ["Land", t("buyerFinder.land", "Land")],
        ["Hotel", t("buyerFinder.hotel", "Hotel")],
        ["Building", t("buyerFinder.building", "Building")],
        ["Development project", t("buyerFinder.developmentProject", "Development project")],
        ["Commercial property", t("buyerFinder.commercialProperty", "Commercial property")],
      ],
      timeframes: [
        ["Immediate", t("buyerFinder.immediate", "Immediate")],
        ["0-3 months", t("buyerFinder.zeroThreeMonths", "0–3 months")],
        ["3-6 months", t("buyerFinder.threeSixMonths", "3–6 months")],
        ["6-12 months", t("buyerFinder.sixTwelveMonths", "6–12 months")],
        ["12+ months", t("buyerFinder.twelvePlusMonths", "12+ months")],
        ["Opportunistic", t("buyerFinder.opportunistic", "Opportunistic")],
      ],
      financing: [
        ["cash", t("buyerFinder.cash", "Cash")],
        ["mortgage", t("buyerFinder.mortgage", "Mortgage")],
        ["cash_and_mortgage", t("buyerFinder.cashMortgage", "Cash and mortgage")],
        ["undecided", t("buyerFinder.undecided", "Undecided")],
      ],
      purposes: [
        ["primary_residence", t("buyerFinder.primaryResidence", "Primary residence")],
        ["holiday_home", t("buyerFinder.holidayHome", "Holiday home")],
        ["rental_income", t("buyerFinder.rentalIncome", "Rental income")],
        ["development", t("buyerFinder.development", "Property development")],
        ["relocation", t("buyerFinder.relocation", "Relocation")],
        ["commercial_investment", t("buyerFinder.commercialInvestment", "Commercial investment")],
        ["other", t("buyerFinder.other", "Other")],
      ],
    }),
    [t]
  );

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ loading: true, error: "", result: null });

    if (!form.gdpr_consent) {
      setStatus({
        loading: false,
        error: t("buyerFinder.consentRequired", "Please accept the privacy consent."),
        result: null,
      });
      return;
    }

    if (!turnstileToken) {
      setStatus({
        loading: false,
        error: t("completeSecurityCheck", "Please complete the Cloudflare security check."),
        result: null,
      });
      return;
    }

    try {
      const payload = {
        ...form,
        campaign_country: campaign.name,
        preferred_language: language,
        turnstile_token: turnstileToken,
      };

      const response = await api.post("/public/buyer-leads", payload);
      setStatus({ loading: false, error: "", result: response.data });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setTurnstileReset((value) => value + 1);
      setStatus({
        loading: false,
        error:
          formatApiErrorDetail(error.response?.data?.detail) ||
          t("buyerFinder.submitError", "Your request could not be submitted. Please try again."),
        result: null,
      });
    }
  };

  if (status.result) {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center p-5">
        <section className="w-full max-w-2xl bg-white border border-slate-200 shadow-xl p-8 md:p-12 text-center">
          <img
            src="/panatlantik-logo.png"
            alt="Panatlantik International"
            className="h-20 mx-auto mb-8"
          />
          <CheckCircle size={64} weight="duotone" className="mx-auto text-emerald-600" />
          <h1 className="font-serif text-3xl text-[#0B2545] mt-5">
            {t("buyerFinder.successTitle", "Your property search has started")}
          </h1>
          <p className="text-slate-600 mt-4">
            {t(
              "buyerFinder.successText",
              "Our team has received your requirements and will review the strongest property matches."
            )}
          </p>
          <div className="mt-6 bg-slate-50 border border-slate-200 p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">
              {t("buyerFinder.reference", "Reference")}
            </div>
            <div className="font-semibold text-[#0B2545] mt-1">
              {status.result.reference}
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F6F7F9]">
      <header className="bg-[#0B2545] text-white">
        <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <img
            src="/panatlantik-logo.png"
            alt="Panatlantik International Real Estate"
            className="h-16 object-contain"
          />
          <div className="flex items-center gap-2">
            <Globe size={18} className="text-[#C5A059]" />
            <select
              value={language}
              onChange={(event) => i18n.changeLanguage(event.target.value)}
              className="bg-[#0B2545] border border-white/30 px-3 py-2 text-sm"
              aria-label="Language"
            >
              {supportedLanguages.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <section className="bg-[#0B2545] text-white border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 py-12 md:py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[#D9B766] uppercase tracking-[0.18em] text-sm">
              {t("buyerFinder.internationalDesk", "International Buyer Desk")}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              {t("buyerFinder.heroTitle", "Looking to buy property in Portugal?")}
            </h1>
            <p className="text-blue-100 text-lg mt-5 max-w-xl">
              {t(
                "buyerFinder.heroText",
                "Tell us what you are looking for. Our team will assess your requirements and identify suitable opportunities across Portugal."
              )}
            </p>
            <p className="text-sm text-blue-200 mt-5">
              {t("buyerFinder.campaignFor", "Buyer service for")}{" "}
              <strong className="text-white">{campaign.name}</strong>
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            {[
              [House, "buyerFinder.benefitMatching", "Property matching"],
              [ShieldCheck, "buyerFinder.benefitConfidential", "Confidential service"],
              [Globe, "buyerFinder.benefitMultilingual", "Multilingual support"],
            ].map(([Icon, key, fallback]) => (
              <div key={key} className="bg-white/10 border border-white/15 p-5">
                <Icon size={28} className="text-[#D9B766]" />
                <div className="font-medium mt-3">{t(key, fallback)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 py-10 md:py-14">
        <form onSubmit={submit} className="bg-white border border-slate-200 shadow-lg p-6 md:p-10">
          <h2 className="font-serif text-3xl text-[#0B2545]">
            {t("buyerFinder.formTitle", "Register your buying requirements")}
          </h2>
          <p className="text-slate-600 mt-2 mb-8">
            {t(
              "buyerFinder.formIntro",
              "We only request information needed to understand your property search. Do not submit passport or identity-document details."
            )}
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label={t("buyerFinder.fullName", "Full name")} required>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className={fieldClass} required />
            </Field>
            <Field label={t("buyerFinder.email", "Email address")} required>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={fieldClass} required />
            </Field>
            <Field label={t("buyerFinder.phone", "Telephone / WhatsApp")}>
              <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={fieldClass} />
            </Field>
            <Field label={t("buyerFinder.nationality", "Nationality")}>
              <Input value={form.nationality} onChange={(e) => set("nationality", e.target.value)} className={fieldClass} />
            </Field>
            <Field label={t("buyerFinder.currentCountry", "Current country")} required>
              <Input value={form.current_country} onChange={(e) => set("current_country", e.target.value)} className={fieldClass} required />
            </Field>
            <Field label={t("buyerFinder.locations", "Preferred locations in Portugal")} required>
              <Input value={form.locations} onChange={(e) => set("locations", e.target.value)} placeholder="Lisbon, Porto, Algarve…" className={fieldClass} required />
            </Field>
            <Field label={t("buyerFinder.budgetMin", "Minimum budget (€)")}>
              <Input type="number" min="0" value={form.budget_min} onChange={(e) => set("budget_min", e.target.value)} className={fieldClass} />
            </Field>
            <Field label={t("buyerFinder.budgetMax", "Maximum budget (€)")} required>
              <Input type="number" min="0" value={form.budget_max} onChange={(e) => set("budget_max", e.target.value)} className={fieldClass} required />
            </Field>

            <SelectField
              label={t("buyerFinder.propertyType", "Property type")}
              value={form.property_types}
              onChange={(value) => set("property_types", value)}
              options={selectOptions.propertyTypes}
              placeholder={t("buyerFinder.selectOption", "Select an option")}
              required
            />
            <SelectField
              label={t("buyerFinder.timeframe", "Purchasing timeframe")}
              value={form.purchase_timeframe}
              onChange={(value) => set("purchase_timeframe", value)}
              options={selectOptions.timeframes}
              placeholder={t("buyerFinder.selectOption", "Select an option")}
              required
            />
            <SelectField
              label={t("buyerFinder.financing", "How will you purchase?")}
              value={form.financing_method}
              onChange={(value) => set("financing_method", value)}
              options={selectOptions.financing}
              placeholder={t("buyerFinder.selectOption", "Select an option")}
              required
            />
            <SelectField
              label={t("buyerFinder.purpose", "Purpose of purchase")}
              value={form.purchase_purpose}
              onChange={(value) => set("purchase_purpose", value)}
              options={selectOptions.purposes}
              placeholder={t("buyerFinder.selectOption", "Select an option")}
              required
            />
          </div>

          <div className="mt-5">
            <Label>{t("buyerFinder.message", "Additional requirements")}</Label>
            <Textarea
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              rows={4}
              className="mt-1.5 rounded-sm border-slate-300 focus-visible:ring-1 focus-visible:ring-[#C5A059]"
            />
          </div>

          <div className="mt-5">
            <Label>{t("buyerFinder.referralCode", "Referral code (optional)")}</Label>
            <Input
              value={form.referral_code}
              onChange={(e) => set("referral_code", e.target.value)}
              className={fieldClass}
            />
          </div>

          <div className="absolute left-[-10000px]" aria-hidden="true">
            <Label htmlFor="buyer-website">Website</Label>
            <Input
              id="buyer-website"
              tabIndex="-1"
              autoComplete="off"
              value={form.website}
              onChange={(e) => set("website", e.target.value)}
            />
          </div>

          <label className="mt-6 flex items-start gap-3 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={form.gdpr_consent}
              onChange={(e) => set("gdpr_consent", e.target.checked)}
              className="mt-1 accent-[#0B2545]"
              required
            />
            <span>
              {t(
                "buyerFinder.consent",
                "I consent to Panatlantik processing my information to respond to this property enquiry and provide relevant property opportunities. I may withdraw my consent at any time."
              )}
            </span>
          </label>

          <div className="mt-7">
            <TurnstileWidget
              onVerify={setTurnstileToken}
              resetKey={turnstileReset}
              language={language === "zh" ? "zh-cn" : language}
            />
          </div>

          {status.error && (
            <div role="alert" className="mt-5 p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
              {status.error}
            </div>
          )}

          <Button
            type="submit"
            disabled={status.loading}
            className="mt-7 w-full h-12 rounded-sm bg-[#0B2545] hover:bg-[#173B63] text-white"
          >
            {status.loading
              ? t("buyerFinder.submitting", "Submitting…")
              : t("buyerFinder.submit", "Find matching properties")}
          </Button>

          <p className="text-xs text-slate-500 mt-4 text-center">
            {t(
              "buyerFinder.noGuarantee",
              "Registration does not constitute financial, legal or immigration advice and does not guarantee availability or financing."
            )}
          </p>
        </form>
      </section>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label>
        {label}
        {required ? " *" : ""}
      </Label>
      {children}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
}) {
  return (
    <div>
      <Label>
        {label}
        {required ? " *" : ""}
      </Label>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={`${fieldClass} w-full bg-white px-3 text-sm`}
        required={required}
      >
        <option value="">{placeholder}</option>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </div>
  );
}
